import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle, Clock, ArrowRight, Home, Calendar, Ticket } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useTranslations } from '../context/TranslationsContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PaymentStatusPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const { td: tCms } = useTranslations(['common', 'payment', 'booking', 'passes']);
  const { token } = useAuth();
  const lang = i18n.language;
  
  // Translation helper
  const t = (key, fallback) => {
    const cmsValue = tCms(key);
    return (cmsValue && cmsValue !== key) ? cmsValue : fallback;
  };

  const [status, setStatus] = useState('loading'); // loading, success, error, cancelled
  const [orderDetails, setOrderDetails] = useState(null);

  const paymentStatus = searchParams.get('status');
  const type = searchParams.get('type'); // booking or pass
  const orderId = searchParams.get('id');
  const error = searchParams.get('error');
  const isGuest = searchParams.get('isGuest') === 'true';

  useEffect(() => {
    const checkPaymentStatus = async () => {
      // If error param is present, show error
      if (error || paymentStatus === 'error' || paymentStatus === 'cancelled') {
        setStatus(paymentStatus === 'cancelled' ? 'cancelled' : 'error');
        return;
      }

      // If success param is present
      if (paymentStatus === 'success' && orderId && token) {
        try {
          const response = await axios.get(`${API}/payments/status/${orderId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          setOrderDetails(response.data);
          
          if (response.data.paymentStatus === 'paid') {
            setStatus('success');
          } else if (response.data.paymentStatus === 'cancelled') {
            setStatus('cancelled');
          } else {
            // Payment pending - might take a moment
            setStatus('pending');
            // Check again after 3 seconds
            setTimeout(checkPaymentStatus, 3000);
          }
        } catch (err) {
          console.error('Failed to check payment status:', err);
          // Still show success - PayU webhook might not have arrived yet
          setStatus('success');
        }
      } else if (paymentStatus === 'success') {
        setStatus('success');
      } else {
        setStatus('error');
      }
    };

    // Small delay to show loading state
    setTimeout(checkPaymentStatus, 1000);
  }, [paymentStatus, orderId, token, error]);

  const getIcon = () => {
    switch (status) {
      case 'success':
        return <CheckCircle className="w-24 h-24 text-green-500" />;
      case 'error':
        return <XCircle className="w-24 h-24 text-red-500" />;
      case 'cancelled':
        return <XCircle className="w-24 h-24 text-yellow-500" />;
      case 'pending':
        return <Clock className="w-24 h-24 text-primary animate-pulse" />;
      default:
        return (
          <div className="w-24 h-24 rounded-full border-4 border-primary/30 border-t-primary animate-spin" />
        );
    }
  };

  const getTitle = () => {
    switch (status) {
      case 'success':
        return t('payment.success', lang === 'pl' ? 'Płatność zakończona pomyślnie!' : 'Payment successful!');
      case 'error':
        return t('payment.failed', lang === 'pl' ? 'Płatność nie powiodła się' : 'Payment failed');
      case 'cancelled':
        return t('payment.cancelled', lang === 'pl' ? 'Płatność anulowana' : 'Payment cancelled');
      case 'pending':
        return t('payment.pending', lang === 'pl' ? 'Oczekiwanie na potwierdzenie...' : 'Awaiting confirmation...');
      default:
        return t('payment.processing', lang === 'pl' ? 'Przetwarzanie płatności...' : 'Processing payment...');
    }
  };

  const getMessage = () => {
    switch (status) {
      case 'success':
        if (type === 'booking') {
          return t('payment.bookingSuccess', lang === 'pl' 
            ? 'Twoja rezerwacja została opłacona. Potwierdzenie wysłaliśmy na Twój adres email.'
            : 'Your booking has been paid. Confirmation has been sent to your email.');
        } else if (type === 'pass') {
          if (isGuest) {
            return t('payment.guestPassSuccess', lang === 'pl'
              ? 'Twój karnet-prezent został zakupiony! Kod karnetu został wysłany na adres email obdarowanej osoby.'
              : 'Your gift pass has been purchased! The pass code has been sent to the recipient\'s email.');
          }
          return t('payment.passSuccess', lang === 'pl'
            ? 'Twój karnet został zakupiony. Szczegóły znajdziesz w zakładce "Moje konto".'
            : 'Your pass has been purchased. Find details in "My Account".');
        }
        return t('payment.thankYou', lang === 'pl' ? 'Dziękujemy za płatność!' : 'Thank you for your payment!');
      case 'error':
        return t('payment.errorMessage', lang === 'pl'
          ? 'Coś poszło nie tak podczas przetwarzania płatności. Spróbuj ponownie lub skontaktuj się z nami.'
          : 'Something went wrong while processing your payment. Please try again or contact us.');
      case 'cancelled':
        return t('payment.cancelledMessage', lang === 'pl'
          ? 'Płatność została anulowana. Twoja rezerwacja nie została opłacona.'
          : 'Payment was cancelled. Your booking has not been paid.');
      case 'pending':
        return t('payment.pendingMessage', lang === 'pl'
          ? 'Trwa weryfikacja płatności. Może to potrwać kilka sekund...'
          : 'Payment verification in progress. This may take a few seconds...');
      default:
        return t('payment.processingMessage', lang === 'pl'
          ? 'Proszę czekać, trwa przetwarzanie płatności...'
          : 'Please wait, processing your payment...');
    }
  };

  return (
    <div className="min-h-screen pt-32 pb-20 px-6 md:px-12 flex items-center justify-center">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="glass-card p-12 max-w-lg w-full text-center"
      >
        {/* Icon */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
          className="flex justify-center mb-8"
        >
          {getIcon()}
        </motion.div>

        {/* Title */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className={`text-3xl font-bold mb-4 ${
            status === 'success' ? 'text-green-500' :
            status === 'error' ? 'text-red-500' :
            status === 'cancelled' ? 'text-yellow-500' :
            'text-foreground'
          }`}
        >
          {getTitle()}
        </motion.h1>

        {/* Message */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="text-muted-foreground mb-8"
        >
          {getMessage()}
        </motion.p>

        {/* Order details */}
        {orderDetails && status === 'success' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-black/5 dark:bg-white/5 rounded-xl p-4 mb-8"
          >
            {/* Pass purchase details */}
            {type === 'pass' && orderDetails.passName && (
              <p className="text-sm text-muted-foreground mb-2">
                <span className="font-medium">{orderDetails.passName}</span>
              </p>
            )}
            {orderDetails.passCode && (
              <p className="text-sm text-muted-foreground mb-2">
                {lang === 'pl' ? 'Kod karnetu:' : 'Pass code:'} <span className="font-mono font-bold text-primary">{orderDetails.passCode}</span>
              </p>
            )}
            {orderDetails.isGift && orderDetails.giftRecipientEmail && (
              <p className="text-sm text-muted-foreground mb-2">
                {lang === 'pl' ? 'Wysłano na:' : 'Sent to:'} <span className="font-medium">{orderDetails.giftRecipientEmail}</span>
              </p>
            )}
            
            {/* Price display */}
            {(orderDetails.price !== undefined && orderDetails.price !== null) && (
              <p className="text-sm text-muted-foreground">
                {lang === 'pl' ? 'Kwota' : 'Amount'}: <span className="font-bold text-primary">{orderDetails.price} zł</span>
              </p>
            )}
            
            {/* Booking details */}
            {type === 'booking' && orderDetails.bookingDetails && (
              <div className="mt-2 text-sm text-muted-foreground">
                {orderDetails.bookingDetails.groupName && (
                  <p>{orderDetails.bookingDetails.groupName} - {orderDetails.bookingDetails.classTypeName}</p>
                )}
                {orderDetails.bookingDetails.date && (
                  <p>{orderDetails.bookingDetails.date} {orderDetails.bookingDetails.time}</p>
                )}
              </div>
            )}
            
            {orderDetails.paidAt && (
              <p className="text-sm text-muted-foreground mt-1">
                {lang === 'pl' ? 'Data płatności' : 'Payment date'}: {new Date(orderDetails.paidAt).toLocaleString(lang === 'pl' ? 'pl-PL' : 'en-US')}
              </p>
            )}
          </motion.div>
        )}

        {/* Actions */}
        {(status === 'success' || status === 'error' || status === 'cancelled') && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            {status === 'success' ? (
              <>
                {!isGuest && (
                  <button
                    onClick={() => navigate('/account')}
                    className="btn-primary flex items-center justify-center gap-2"
                  >
                    {type === 'pass' ? <Ticket className="w-5 h-5" /> : <Calendar className="w-5 h-5" />}
                    {lang === 'pl' ? 'Moje konto' : 'My Account'}
                  </button>
                )}
                <button
                  onClick={() => navigate('/')}
                  className={`${isGuest ? 'btn-primary' : 'btn-secondary'} flex items-center justify-center gap-2`}
                >
                  <Home className="w-5 h-5" />
                  {lang === 'pl' ? 'Strona główna' : 'Home'}
                </button>
              </>
            ) : (
              <>
                {/* "Try again" button removed per user request - failed payments should be handled via contact */}
                <button
                  onClick={() => navigate('/contact')}
                  className="btn-primary flex items-center justify-center gap-2"
                >
                  {lang === 'pl' ? 'Skontaktuj się z nami' : 'Contact us'}
                </button>
              </>
            )}
          </motion.div>
        )}
      </motion.div>
    </div>
  );
};

export default PaymentStatusPage;
